import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, Image as ImageIcon, Loader2, Mic, Video } from "lucide-react";
import { fetchPublicGroupFiles } from "../lib/api";

const KIND_ICON = { document: FileText, image: ImageIcon, video: Video, audio: Mic };

function fmtSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return n ? `${n} B` : "";
}

function fmtDate(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function FileRow({ file }) {
  const Icon = KIND_ICON[file.kind] || FileText;
  const meta = [fmtSize(file.size), fmtDate(file.created_at)].filter(Boolean).join(" · ");
  return (
    <li>
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm hover:bg-slate-50"
        data-testid="group-file-row"
      >
        {file.kind === "image" ? (
          <img src={file.url} alt="" loading="lazy" className="h-11 w-11 shrink-0 rounded-lg object-cover" />
        ) : (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Icon size={20} />
          </span>
        )}
        <span className="min-w-0 grow">
          <span className="block truncate text-[15px] font-medium text-slate-900">{file.file_name || "File"}</span>
          {meta && <span className="block text-[13px] text-slate-500">{meta}</span>}
        </span>
      </a>
    </li>
  );
}

// The sections, in journey order. Files recorded before the page had stages are
// all inventory, so a file with no stage lands in the first section.
const SECTIONS = [
  { stage: "media", title: "Inventory" },
  { stage: "work_progress", title: "Work progress" },
  { stage: "delivery_media", title: "Delivery" },
];

function sectionsOf(files) {
  return SECTIONS.map((s) => ({
    ...s,
    files: files.filter((f) => (SECTIONS.some((x) => x.stage === f.stage) ? f.stage : "media") === s.stage),
  })).filter((s) => s.files.length > 0);
}

/** What a customer sees from the link in their WhatsApp group: the files the
 * service desk has sent them, a section per journey stage. Public -- no login;
 * the token is the credential. */
export default function GroupFiles() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let live = true;
    setState({ status: "loading" });
    fetchPublicGroupFiles(token)
      .then((data) => live && setState({ status: "ok", data }))
      .catch((err) => live && setState({ status: err?.response?.status === 404 ? "missing" : "error" }));
    return () => {
      live = false;
    };
  }, [token]);

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-6" data-testid="group-files-page">
      <div className="mx-auto max-w-xl">
        {state.status === "loading" && (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-slate-400" />
          </div>
        )}
        {state.status === "missing" && (
          <p className="py-16 text-center text-slate-600" data-testid="group-files-missing">
            This link isn't valid.
          </p>
        )}
        {state.status === "error" && (
          <p className="py-16 text-center text-slate-600">Couldn't load the files. Please try again.</p>
        )}
        {state.status === "ok" && (
          <>
            <h1 className="m-0 mb-4 text-xl font-semibold text-slate-900">{state.data.group_name || "Files"}</h1>
            {state.data.files.length === 0 ? (
              <p className="py-10 text-center text-slate-600" data-testid="group-files-empty">
                No files have been shared yet.
              </p>
            ) : (
              sectionsOf(state.data.files).map((s) => (
                <section key={s.stage} className="mb-6" data-testid={`group-files-section-${s.stage}`}>
                  <h2 className="m-0 mb-2 text-[15px] font-semibold text-slate-700">{s.title}</h2>
                  <ul className="m-0 flex list-none flex-col gap-2 p-0">
                    {s.files.map((f) => (
                      <FileRow key={f.id} file={f} />
                    ))}
                  </ul>
                </section>
              ))
            )}
          </>
        )}
      </div>
    </main>
  );
}
